/**
 * ChatHistoryRail — collapsible saved-chats + folders rail for the "New Task"
 * page. Reuses the same backend (ChatSession/ChatFolder via chatSessionAPI)
 * and the same rename/move/delete menu (ChatContextMenu) the Conversations
 * page already uses — this is a lighter, page-native front end over the
 * identical data, not a parallel system.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ChevronsLeft, ChevronsRight, Plus, Search, MessageSquare, MoreHorizontal, FolderPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { chatSessionAPI } from '../../services/api.js';
import { ChatContextMenu } from '../../pages/ConversationsPage.jsx';
import { T2 } from './AppShellV2.tsx';

const COLLAPSE_KEY = 'lyra:aiAssistantRail:collapsed';

function timeAgo(dateStr?: string) {
  if (!dateStr) return '';
  const ms = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

interface Session {
  _id: string;
  title: string;
  folderId: string | null;
  updatedAt: string;
  messageCount: number;
}
interface Folder {
  _id: string;
  name: string;
  color?: string;
}

export default function ChatHistoryRail({
  activeSessionId,
  onSelect,
  onNewChat,
  refreshKey,
}: {
  activeSessionId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  refreshKey: number;
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === 'true'; } catch { return false; }
  });
  const [sessions, setSessions] = useState<Session[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [sRes, fRes]: any[] = await Promise.all([chatSessionAPI.list(), chatSessionAPI.listFolders()]);
      setSessions(sRes?.sessions || []);
      setFolders(fRes?.folders || []);
    } catch (_) {
      // non-fatal — rail just shows empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, String(collapsed)); } catch {}
  }, [collapsed]);

  const q = query.trim().toLowerCase();
  const filtered = q ? sessions.filter((s) => (s.title || '').toLowerCase().includes(q)) : sessions;

  const grouped: { folder: Folder | null; sessions: Session[] }[] = [
    ...folders.map((f) => ({ folder: f, sessions: filtered.filter((s) => s.folderId === f._id) })),
    { folder: null, sessions: filtered.filter((s) => !s.folderId) },
  ].filter((g) => g.sessions.length > 0 || !q);

  const menuFolders = folders.map((f) => ({ id: f._id, name: f.name }));

  const handleRenamed = (id: string, title: string) => {
    setSessions((prev) => prev.map((s) => (s._id === id ? { ...s, title } : s)));
  };
  const handleMoved = (id: string, folderId: string) => {
    setSessions((prev) => prev.map((s) => (s._id === id ? { ...s, folderId: folderId === '__none__' ? null : folderId } : s)));
  };
  const handleDeleted = (id: string) => {
    setSessions((prev) => prev.filter((s) => s._id !== id));
    if (id === activeSessionId) onNewChat();
  };

  const createFolder = async () => {
    const name = window.prompt('Folder name:');
    if (!name?.trim()) return;
    try {
      const res: any = await chatSessionAPI.createFolder({ name: name.trim() });
      const folder = res?.folder || res;
      if (folder?._id) setFolders((prev) => [...prev, folder]);
      toast.success('Folder created');
    } catch (_) {
      toast.error('Failed to create folder');
    }
  };

  if (collapsed) {
    return (
      <div style={{ width: 44, flexShrink: 0, borderRight: `1px solid ${T2.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12, gap: 8 }}>
        <button
          onClick={() => setCollapsed(false)}
          title="Show chat history"
          style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: T2.ink2, borderRadius: 8, cursor: 'pointer' }}
        >
          <ChevronsRight size={16} />
        </button>
        <button
          onClick={onNewChat}
          title="New chat"
          style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: T2.accentText, borderRadius: 8, cursor: 'pointer' }}
        >
          <Plus size={16} />
        </button>
        <button
          onClick={createFolder}
          title="New folder"
          style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: T2.ink3, borderRadius: 8, cursor: 'pointer' }}
        >
          <FolderPlus size={16} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: 240, flexShrink: 0, borderRight: `1px solid ${T2.border}`, display: 'flex', flexDirection: 'column', background: T2.surface }}>
      <div style={{ padding: '12px 12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T2.ink }}>Chats</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button
            onClick={createFolder}
            title="New folder"
            style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: T2.ink3, borderRadius: 7, cursor: 'pointer' }}
          >
            <FolderPlus size={14} />
          </button>
          <button
            onClick={() => setCollapsed(true)}
            title="Collapse"
            style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: T2.ink3, borderRadius: 7, cursor: 'pointer' }}
          >
            <ChevronsLeft size={15} />
          </button>
        </div>
      </div>

      <div style={{ padding: '0 12px 10px' }}>
        <button
          onClick={onNewChat}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            border: 'none', background: T2.accent, color: 'var(--app-on-accent)', borderRadius: 9, padding: '8px 0',
            fontSize: 12.5, fontWeight: 600, cursor: 'pointer', marginBottom: 8,
          }}
        >
          <Plus size={13} /> New chat
        </button>
        <div style={{ position: 'relative' }}>
          <Search size={13} color={T2.ink3} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats..."
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px 6px 28px', border: `1px solid ${T2.border}`, borderRadius: 8, fontSize: 12, outline: 'none', background: T2.page, color: T2.ink }}
          />
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 8px 12px' }}>
        {loading ? (
          <div style={{ padding: '10px 6px', fontSize: 12, color: T2.ink3 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '10px 6px', fontSize: 12, color: T2.ink3 }}>
            {q ? `No chats matching "${query}"` : 'No saved chats yet — send a message to start one.'}
          </div>
        ) : (
          grouped.map((g) => (
            <div key={g.folder?._id || '__uncategorized__'} style={{ marginBottom: 10 }}>
              {g.folder && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', fontSize: 10.5, fontWeight: 700, color: T2.ink3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: g.folder.color || T2.accent, flexShrink: 0 }} />
                  {g.folder.name}
                </div>
              )}
              {g.sessions.map((s) => {
                const active = s._id === activeSessionId;
                return (
                  <div
                    key={s._id}
                    onClick={() => onSelect(s._id)}
                    style={{
                      position: 'relative', display: 'flex', alignItems: 'center', gap: 8,
                      padding: '7px 8px', borderRadius: 8, cursor: 'pointer',
                      background: active ? T2.accentSoft : 'transparent',
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = T2.page; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <MessageSquare size={13} color={active ? T2.accentText : T2.ink3} style={{ flexShrink: 0 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: active ? 700 : 500, color: active ? T2.accentText : T2.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.title || 'New Chat'}
                      </div>
                      <div style={{ fontSize: 10.5, color: T2.ink3 }}>{timeAgo(s.updatedAt)}</div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setOpenMenuId(s._id === openMenuId ? null : s._id); }}
                      style={{ flexShrink: 0, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: T2.ink3, borderRadius: 6, cursor: 'pointer' }}
                    >
                      <MoreHorizontal size={14} />
                    </button>
                    {openMenuId === s._id && (
                      <ChatContextMenu
                        sessionId={s._id}
                        currentTitle={s.title || 'New Chat'}
                        folders={menuFolders}
                        onClose={() => setOpenMenuId(null)}
                        onRenamed={handleRenamed}
                        onMoved={handleMoved}
                        onDeleted={handleDeleted}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
